import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { loadPlaces, parseAddressComponents, isPlacesConfigured } from '../lib/googlePlaces';

const DEBOUNCE_MS = 250;

/**
 * Address input backed by Google Places Autocomplete Data API (AU only).
 * Falls back to a plain text input if Google cannot load.
 *
 * onSelect({ formattedAddress, street, suburb, state, postcode })
 */
export const AddressAutocomplete = ({ value, onChange, onSelect, inputProps = {} }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [available, setAvailable] = useState(isPlacesConfigured());
  const placesRef = useRef(null);      // { AutocompleteSessionToken, AutocompleteSuggestion }
  const sessionRef = useRef(null);     // current billing session token
  const requestIdRef = useRef(0);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  const ensurePlaces = async () => {
    if (placesRef.current) return placesRef.current;
    try {
      placesRef.current = await loadPlaces();
      return placesRef.current;
    } catch (err) {
      console.warn('Address autocomplete unavailable:', err.message);
      setAvailable(false);
      return null;
    }
  };

  const fetchSuggestions = async (text) => {
    const places = await ensurePlaces();
    if (!places) return;
    if (!sessionRef.current) sessionRef.current = new places.AutocompleteSessionToken();
    const requestId = ++requestIdRef.current;
    try {
      const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: text,
        includedRegionCodes: ['au'],
        sessionToken: sessionRef.current,
      });
      if (requestId !== requestIdRef.current) return; // stale response
      const list = results.filter((s) => s.placePrediction).slice(0, 6);
      setSuggestions(list);
      setOpen(list.length > 0);
      setActiveIndex(-1);
    } catch (err) {
      console.warn('Address suggestions failed:', err.message);
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    clearTimeout(timerRef.current);
    if (!available || text.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  const handleSelect = async (suggestion) => {
    const prediction = suggestion.placePrediction;
    const place = prediction.toPlace();
    setOpen(false);
    setSuggestions([]);
    try {
      await place.fetchFields({ fields: ['formattedAddress', 'addressComponents'] });
      const parsed = parseAddressComponents(place.addressComponents);
      const formatted = place.formattedAddress || prediction.text.text;
      onChange(formatted);
      onSelect?.({ formattedAddress: formatted, ...parsed });
    } catch (err) {
      console.warn('Place details failed:', err.message);
      onChange(prediction.text.text);
    } finally {
      sessionRef.current = null; // session ends after fetchFields
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="address-autocomplete" ref={wrapperRef}>
      <input
        {...inputProps}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => { ensurePlaces(); if (suggestions.length) setOpen(true); }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="address-suggestions"
        aria-activedescendant={activeIndex >= 0 ? `address-option-${activeIndex}` : undefined}
      />
      {open && (
        <ul id="address-suggestions" className="suburb-suggestions address-suggestions" role="listbox" data-testid="address-suggestions">
          {suggestions.map((s, i) => {
            const main = s.placePrediction.mainText?.text || s.placePrediction.text.text;
            const secondary = s.placePrediction.secondaryText?.text || '';
            return (
              <li
                key={s.placePrediction.placeId}
                id={`address-option-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`suburb-suggestion address-suggestion${i === activeIndex ? ' is-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                onMouseEnter={() => setActiveIndex(i)}
                data-testid={`address-option-${i}`}
              >
                <MapPin size={16} className="address-suggestion-pin" aria-hidden="true" />
                <span className="address-suggestion-text">
                  <span className="suburb-suggestion-name">{main}</span>
                  {secondary && <span className="address-suggestion-secondary">{secondary}</span>}
                </span>
              </li>
            );
          })}
          <li className="address-attribution" aria-hidden="true">Powered by Google</li>
        </ul>
      )}
    </div>
  );
};
